import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

/**
 * The create wizard's schedule step.
 *
 * Two jobs:
 *
 * 1. Compose. The step asks for a date and two clock times, because that is how
 *    staff describe a recurring event ("Tuesdays, 5 to 7"). The server still
 *    wants `starts_at` / `ends_at` datetimes, so this writes the hidden fields
 *    from the three visible ones. Nothing about the create action changes.
 *
 * 2. Preview. Any schedule edit re-requests the panel from the server, which
 *    renders it from the same expander that will create the occurrences. The
 *    dates are never computed here — a second implementation would drift from
 *    the first, which is the bug this step exists to fix.
 *
 * Usage:
 *   <div data-controller="wizard-schedule"
 *        data-wizard-schedule-url-value="/events/schedule_preview">
 *     <input type="date" data-wizard-schedule-target="date"
 *            data-action="input->wizard-schedule#change">
 *     <input type="time" data-wizard-schedule-target="startTime" …>
 *     <input type="time" data-wizard-schedule-target="endTime" …>
 *     <input type="hidden" name="event[starts_at]" data-wizard-schedule-target="startsAt">
 *     <input type="hidden" name="event[ends_at]"   data-wizard-schedule-target="endsAt">
 *   </div>
 */
// Only the fields that can change which dates get generated. Sending the whole
// form would post the name and description on every keystroke.
const SCHEDULE_FIELD =
  /^event\[(event_type|frequency|time_zone|starts_at|ends_at|recurrence_config_attributes|series_parts_attributes)\]/

export default class extends Controller {
  static targets = ["date", "startTime", "endTime", "startsAt", "endsAt"]
  static values = {
    url: String,
    debounce: { type: Number, default: 300 },
  }

  declare readonly hasDateTarget: boolean
  declare readonly dateTarget: HTMLInputElement
  declare readonly hasStartTimeTarget: boolean
  declare readonly startTimeTarget: HTMLInputElement
  declare readonly hasEndTimeTarget: boolean
  declare readonly endTimeTarget: HTMLInputElement
  declare readonly hasStartsAtTarget: boolean
  declare readonly startsAtTarget: HTMLInputElement
  declare readonly hasEndsAtTarget: boolean
  declare readonly endsAtTarget: HTMLInputElement
  declare urlValue: string
  declare debounceValue: number

  private timer: number | undefined

  connect() {
    this.compose()
  }

  disconnect() {
    if (this.timer) window.clearTimeout(this.timer)
  }

  /** A visible schedule field changed: recompose, then refresh the preview. */
  change() {
    this.compose()
    this.schedule()
  }

  /** A field the server reads directly (weekday, interval, type) changed. */
  refresh() {
    this.schedule()
  }

  private schedule() {
    if (this.timer) window.clearTimeout(this.timer)
    this.timer = window.setTimeout(() => this.load(), this.debounceValue)
  }

  // Writes the hidden datetimes the create action consumes. A blank date
  // clears both — a time on its own is not a schedule.
  private compose() {
    if (!this.hasStartsAtTarget || !this.hasDateTarget) return

    const date = this.dateTarget.value
    if (!date) {
      this.startsAtTarget.value = ""
      if (this.hasEndsAtTarget) this.endsAtTarget.value = ""
      return
    }

    const start = this.hasStartTimeTarget && this.startTimeTarget.value ? this.startTimeTarget.value : "00:00"
    this.startsAtTarget.value = `${date}T${start}`

    if (!this.hasEndsAtTarget) return
    const end = this.hasEndTimeTarget ? this.endTimeTarget.value : ""
    this.endsAtTarget.value = end ? `${this.endDate(date, start, end)}T${end}` : ""
  }

  // An end strictly earlier than the start means the event runs past midnight,
  // so the end belongs to the following day. Without this a 9pm–1am service
  // would submit an end before its start and fail validation.
  //
  // An end *equal* to the start stays on the same day on purpose: that is a
  // zero-length event, which the model rejects. Rolling it forward would turn a
  // typo into a silent 24-hour event.
  private endDate(date: string, start: string, end: string): string {
    if (end >= start) return date

    const next = new Date(`${date}T00:00`)
    next.setDate(next.getDate() + 1)
    return `${next.getFullYear()}-${pad(next.getMonth() + 1)}-${pad(next.getDate())}`
  }

  private async load() {
    const form = this.element.closest("form")
    if (!form || !this.urlValue) return

    const params = new URLSearchParams()
    for (const [key, value] of new FormData(form).entries()) {
      if (typeof value === "string" && SCHEDULE_FIELD.test(key)) params.append(key, value)
    }

    const response = await fetch(`${this.urlValue}?${params.toString()}`, {
      headers: { Accept: "text/vnd.turbo-stream.html" },
      credentials: "same-origin",
    })
    if (!response.ok) return

    Turbo.renderStreamMessage(await response.text())
  }
}

function pad(n: number): string {
  return String(n).padStart(2, "0")
}
